import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { isEqual, uniqWith } from 'lodash-es';
import { BaseNestedFieldDefinition, Coordinate, FieldType, PartialRecord } from '../../../core/public_api';
import { DialogConfig, DialogRef } from '../../../dynamic-dialog/public_api';
import { cacheValueWhileSubscribed } from '../../../helpers/typed-reactive-filters.helper';
import { LocationDialogFailureReason, LocationDialogOutput } from '../../../maps/entities/dialog-output';
import { FieldReadHelper } from '../../../profile-form/public_api';
import { ShRecordsMapComponent } from '../../components/records-map/records-map.component';
import { AbstractGeoSandboxService } from '../../sandbox/geo.abstract.sandbox.service';

@Component({
    selector: 'sh-location-dialog',
    templateUrl: './location-dialog.component.html',
    styleUrls: ['./location-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ShRecordsMapComponent, AsyncPipe]
})
export class ShLocationDialogComponent implements OnInit {
    public record: PartialRecord;
    public coordinate: Coordinate;
    public positionFieldDefinition: BaseNestedFieldDefinition;

    public records$: Observable<PartialRecord[]>;
    private readonly bounds$: BehaviorSubject<google.maps.LatLngBounds | null> =
        new BehaviorSubject<google.maps.LatLngBounds | null>(null);

    constructor(
        public config: DialogConfig,
        private readonly dialog: DialogRef<LocationDialogOutput>,
        private readonly sandbox: AbstractGeoSandboxService,
        private readonly fieldReadHelper: FieldReadHelper
    ) {}

    ngOnInit(): void {
        this.checkLocation();
        this.initRecordsStream();
    }

    private checkLocation(): void {
        this.record = this.config.data.fragment;
        const positionFieldDefinition: BaseNestedFieldDefinition = this.config.data.positionFieldDefinition;

        let coordinate: Coordinate | null = null;

        if (!positionFieldDefinition || positionFieldDefinition.Type !== FieldType.PositionField) {
            console.error('ShLocationDialogComponent:: No position fielddefinition passed to dialog');
            this.dialog.close({ result: 'failed', reason: LocationDialogFailureReason.NO_FIELD_DEFINITION });
            return;
        } else {
            this.positionFieldDefinition = positionFieldDefinition;
            coordinate = this.fieldReadHelper.getPositionFieldCoordinateValue(
                this.positionFieldDefinition,
                this.record
            );
        }

        if (!coordinate) {
            console.error(
                this.sandbox.translate(
                    `ShLocationDialogComponent:: No geocoordinate value found for the record in the ${this.positionFieldDefinition.FlatKey} field`
                )
            );
            this.dialog.close({ result: 'failed', reason: LocationDialogFailureReason.NO_COORDINATE });
            return;
        }

        this.coordinate = coordinate;
    }

    private initRecordsStream(): void {
        const focusRecordCluster$: Observable<PartialRecord[]> = this.getFocusRecordCluster();
        const boundsRecords$: Observable<PartialRecord[]> = this.bounds$.pipe(
            switchMap(bounds => this.getRecordsInBounds(bounds))
        );
        this.records$ = combineLatest([focusRecordCluster$, boundsRecords$]).pipe(
            map(([focusCluster, boundsRecords]) => uniqWith([...focusCluster, ...boundsRecords], isEqual))
        );
    }

    // To prevent flashing of single marker to cluster after opening the map we fetch the cluster before initializing the map
    private getFocusRecordCluster(): Observable<PartialRecord[]> {
        return this.sandbox
            .getGeoLocationCluster(this.positionFieldDefinition, this.coordinate)
            .pipe(take(1), cacheValueWhileSubscribed());
    }

    private getRecordsInBounds(bounds: google.maps.LatLngBounds | null): Observable<PartialRecord[]> {
        if (!bounds) {
            return of([this.record]);
        }

        return this.sandbox.getInBounds(this.positionFieldDefinition, bounds);
    }

    public onBoundsChange(bounds: google.maps.LatLngBounds): void {
        this.bounds$.next(bounds);
    }
}
